import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { isLscBillingUiEnabled } from '../../config/featureFlags';
import { getLscBillingStatus, LscApiError } from '../api';
import type { LscBillingStatusResponse } from '../types';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import BillingStatusCard from '../components/BillingStatusCard';
import FounderBetaPricingCards from '../components/FounderBetaPricingCards';
import BillingDisabledBanner from '../components/BillingDisabledBanner';
import BillingSupportContact from '../billingSupportContact';
import PaidUnboundRecoveryBanner from '../components/PaidUnboundRecoveryBanner';
import LoadingState from '../components/LoadingState';
import { resolveLscBillingPresentation } from '../billingPresentation';
import { resolvePaidUnboundRecovery } from '../paidUnboundRecovery';
import { lscPath, lscReturnTo } from '../routing';
import { useLscContentColumn } from '../lscContentColumn';

const LscBillingPage: React.FC = () => {
  useLscContentColumn('narrow');
  return (
    <LscAuthGate returnTo={lscReturnTo('/billing')} title="Sign in for billing">
      <BillingPageContent />
    </LscAuthGate>
  );
};

function BillingPageContent() {
  const billingUiEnabled = isLscBillingUiEnabled();
  const [billingStatus, setBillingStatus] = useState<LscBillingStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const status = await getLscBillingStatus();
      setBillingStatus(status);
    } catch (err) {
      if (err instanceof LscApiError && (err.status === 401 || err.status === 403)) {
        setBillingStatus(null);
        setLoadError(null);
      } else {
        setLoadError('Billing information is unavailable right now.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const presentation = useMemo(
    () =>
      resolveLscBillingPresentation({
        status: billingStatus,
        surface: 'account_billing',
        viewer: { isAuthenticated: true, isOwner: true },
        context: { billingUiEnabled },
      }),
    [billingStatus, billingUiEnabled]
  );

  const recovery = useMemo(
    () => resolvePaidUnboundRecovery(billingStatus),
    [billingStatus]
  );

  if (loading) {
    return <LoadingState message="Loading billing..." />;
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="lsc-billing-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">{presentation.headline}</h1>
        <p className="text-sm text-secondary-400">{presentation.valueProposition}</p>
      </div>

      {presentation.disabledBanner === 'ui_disabled' && (
        <BillingDisabledBanner variant="ui" message={presentation.disabledReason} />
      )}

      <PaidUnboundRecoveryBanner recovery={recovery} onBound={() => void loadStatus()} />

      <BillingStatusCard
        status={billingStatus}
        loading={false}
        error={loadError}
        onRetry={() => void loadStatus()}
      />

      {presentation.disabledBanner === 'lifecycle' && !presentation.showPricingCards && (
        <BillingDisabledBanner variant="checkout" message={presentation.disabledReason} />
      )}

      {presentation.showPricingCards && (
        <FounderBetaPricingCards
          billingStatus={billingStatus}
          showCheckout={billingUiEnabled}
          presentation={presentation}
        />
      )}

      <p className="text-sm text-secondary-500">
        <Link to={lscPath('/pricing')} className="text-primary-300 hover:text-primary-200">
          View pricing
        </Link>
      </p>

      <BillingSupportContact />
    </div>
  );
}

export default LscBillingPage;
